/* eslint-disable react/prop-types */
import { Close } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid';
import { useState } from 'react';
import { GREY, PRIMARY, RED } from '../../constants/style';
import { useHolidayName } from '../../providers/HolidayNameProvider';
import { useHolidayType } from '../../providers/HolidayTypeProvider';
import {
  ICreateHolidayProps,
  IHolidayTypeListProps,
} from '../../types/holiday';
import { formatDate } from '../../utils/dayjsUtils';
import { EditIcon, InfoIcon, TrashIcon } from '../Icons/Icons';
import LmButton from '../common/LmButton';
import LmDialog from '../common/LmDialog';
import VegaDataGrid from '../common/VegaDataGrid';
import VegaText from '../common/VegaText';
import ViewHolidayDetails from './ViewHolidayDetails';

const ListOfHolidays = () => {
  const {
    holidayListState,
    selectedHoliday,
    setSelectedHoliday,
    toggleAddHolidayNameDrawer,
    onDeleteHoliday,
  } = useHolidayName();
  const { holidayTypeListState } = useHolidayType();
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isView, setIsView] = useState<boolean>(false);

  const toggleDeleteModal = () => {
    setIsDelete(!isDelete);
  };
  const toggleViewDetailsModal = () => {
    setIsView(!isView);
  };

  const COLUMN_DEF: GridColumns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 0.7,
      minWidth: 100,
      cellClassName: 'padding',
      headerClassName: 'padding',
    },
    {
      field: 'holidayType',
      headerName: 'Holiday Type',
      flex: 1,
      minWidth: 150,
      renderCell: props => {
        const holiday = props.row as ICreateHolidayProps;
        const findHolidayTypeName = holidayTypeListState.records.find(
          (holidayType: IHolidayTypeListProps) =>
            holiday.holidayType === holidayType.id
        );
        return (
          <VegaText
            text={findHolidayTypeName?.name}
            fontWeight={500}
            fontSize={'0.875rem'}
          />
        );
      },
    },
    {
      field: 'startDateTime',
      headerName: 'Start Date & Time',
      flex: 1,
      minWidth: 150,
      renderCell: props => {
        const holidayType = props.row as ICreateHolidayProps;
        return (
          <VegaText
            text={formatDate(holidayType.startDateTime)}
            fontWeight={500}
            fontSize={'0.875rem'}
          />
        );
      },
    },
    {
      field: 'endDateTime',
      headerName: 'End Date & Time',
      flex: 1,
      minWidth: 150,
      renderCell: props => {
        const holidayType = props.row as ICreateHolidayProps;
        return (
          <VegaText
            text={formatDate(holidayType.endDateTime)}
            fontWeight={500}
            fontSize={'0.875rem'}
          />
        );
      },
    },

    {
      field: 'typeDetails',
      headerName: 'Holiday Details',
      flex: 0.6,
      minWidth: 80,
      renderCell: props => {
        const holidayType = props.row as ICreateHolidayProps;
        return (
          <LmButton
            text="View Details"
            variant="text"
            textColor={PRIMARY.darkBlue}
            fontWeight={500}
            sx={{
              px: 0,
            }}
            fontSize={'0.875rem'}
            bgColor="transparant"
            onClick={() => viewDetails(holidayType)}
          />
        );
      },
    },
    {
      field: 'action',
      headerName: 'Actions',
      flex: 0.6,
      minWidth: 80,
      renderCell: props => {
        const holidayType = props.row as ICreateHolidayProps;
        const TODAY = new Date();
        const isPastDate = holidayType.endDateTime > TODAY.toISOString();
        return (
          <Stack direction={'row'} gap={1}>
            <LmButton
              text=""
              startIcon={<EditIcon />}
              bgColor="#E7EDFC"
              textColor={PRIMARY.darkBlue}
              onClick={() => {
                if (!holidayType.id) return;
                setSelectedHoliday(holidayType);
                toggleAddHolidayNameDrawer();
              }}
              disabled={!isPastDate}
            />

            <LmButton
              text=""
              startIcon={<TrashIcon />}
              onClick={() => {
                if (!holidayType.id) return;
                setSelectedHoliday(holidayType);
                setIsDelete(true);
              }}
              bgColor="#F8E8E8"
              textColor={RED.red}
              disabled={!isPastDate}
            />
          </Stack>
        );
      },
    },
  ];

  const viewDetails = (id: ICreateHolidayProps | undefined) => {
    if (id) {
      setSelectedHoliday(id);
      toggleViewDetailsModal();
    }
  };

  return (
    <>
      <VegaDataGrid
        data={holidayListState.records}
        columns={COLUMN_DEF}
        idColumn="id"
        loading={holidayListState.loading}
      />
      <LmDialog
        fullWidth
        maxWidth="xs"
        open={isDelete}
        handleClose={toggleDeleteModal}
        buttonComponent={() => (
          <Stack
            width={'100%'}
            direction={'row'}
            alignItems={'center'}
            px={4}
            pb={4}
            gap={1}
          >
            <LmButton
              fullWidth
              text="Delete"
              bgColor={RED.red}
              startIcon={<TrashIcon strokeColor={'white'} />}
              onClick={async () => {
                await onDeleteHoliday();
                toggleDeleteModal();
              }}
            />
            <LmButton
              text="Cancel"
              fullWidth
              variant="outlined"
              bgColor=""
              textColor={GREY.dark}
              onClick={() => {
                setSelectedHoliday(null);
                toggleDeleteModal();
              }}
              startIcon={<Close sx={{ color: GREY.dark }} />}
            />
          </Stack>
        )}
        headerComponent={() => (
          <Stack
            direction={'row'}
            alignItems={'center'}
            px={4}
            py={2.5}
            gap={2}
            bgcolor={'#F8E8E8'}
          >
            <InfoIcon />
            <VegaText
              text="Confirmation"
              fontWeight={600}
              fontSize={'1.0625rem'}
              color={RED.red}
            />
          </Stack>
        )}
      >
        <Box p={4}>
          <VegaText
            text="Are you sure you want to delete this holiday?"
            fontWeight={600}
            fontSize={17}
          />
          <Typography
            className="font-aspekta-400"
            color={'#999DA8'}
            fontSize={'0.8125rem'}
            lineHeight={'15.6px'}
            mt={1}
          >
            This action cannot be undone. This will permanently delete the{' '}
            <b style={{ color: '#1B1D22' }}>{selectedHoliday?.name} </b>{' '}
            instance.
          </Typography>
        </Box>
      </LmDialog>

      <LmDialog
        fullWidth
        maxWidth="sm"
        open={isView}
        isCloseIcon
        handleClose={toggleViewDetailsModal}
        buttonComponent={() => <></>}
        headerComponent={() => (
          <Stack
            direction={'row'}
            alignItems={'center'}
            px={4}
            py={2.5}
            gap={2}
            bgcolor={'#E5F7FC'}
          >
            <VegaText
              text="View Holiday Details"
              fontWeight={600}
              fontSize={'1.0625rem'}
              color={'black'}
            />
          </Stack>
        )}
      >
        <Box px={4} pt={3} pb={4}>
          <ViewHolidayDetails />
        </Box>
      </LmDialog>
    </>
  );
};

export default ListOfHolidays;
